import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import { buildDownloadFilename } from '@/utils/downloadFilename';
import type {
  Customer,
  CustomerAnalytics,
  CustomerInsights,
  Transaction,
  ApiResponse,
  BackendPaginatedResponse
} from '@/types';

export interface LoyaltyLedgerEntry {
  id: string;
  type: 'EARN' | 'REDEEM' | 'EXPIRE' | 'ADJUST' | 'REFUND';
  points: number;
  balanceBefore: number;
  balanceAfter: number;
  note?: string;
  transactionId?: string;
  createdAt: string;
  createdBy?: { id: string; name: string };
}

export interface CustomersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  storeId?: string; // Filter by store for multi-store support
  sortBy?: 'name' | 'loyaltyPoints' | 'totalSpent' | 'lastVisit' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  loyaltyTier?: 'all' | 'bronze' | 'silver' | 'gold' | 'platinum';
}

export interface CreateCustomerData {
  name: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  notes?: string;
  storeId: string; // Mandatory now
}

export interface UpdateCustomerData {
  name?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  notes?: string;
  loyaltyPoints?: number;
}

export interface CustomerStats {
  totalCustomers: number;
  newCustomersThisMonth: number;
  activeCustomers: number;
  topSpenders: Customer[];
  loyaltyDistribution: {
    bronze: number;
    silver: number;
    gold: number;
    platinum: number;
  };
  averageOrderValue: number;
  customerRetentionRate: number;
}

export interface BulkCustomerImportData {
  file: File;
}

export interface BulkCustomerExportParams {
  storeId: string;
  format: 'csv' | 'excel' | 'pdf';
  fields?: string[];
  customerIds?: string[];
  loyaltyTiers?: string[];
  startDate?: string;
  endDate?: string;
}

export interface CustomerTransactionHistory {
  customerId: string;
  transactions: Transaction[];
  summary: {
    totalTransactions: number;
    totalSpent: number;
    averageOrderValue: number;
    lastVisit: string;
    favoriteItems: Array<{
      itemId: string;
      itemName: string;
      quantity: number;
      totalSpent: number;
    }>;
  };
}

// Create a dynamic base query for customer API
const dynamicCustomerBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem('token');
      }
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      // Tell the server which app language is selected (exports and templates are localized)
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const customerApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: dynamicCustomerBaseQuery,
  tagTypes: ['Customer', 'CustomerStats'],
  endpoints: (builder) => ({
    // Get customer statistics
    getCustomerStats: builder.query<ApiResponse<CustomerStats>, { storeId?: string }>({
      query: (params) => ({
        url: '/customers/stats',
        params,
      }),
      providesTags: ['CustomerStats'],
    }),

    // Get all customers with filtering and pagination
    getCustomers: builder.query<ApiResponse<BackendPaginatedResponse<Customer>>, CustomersQueryParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value.toString());
          }
        });
        return `/customers?${searchParams.toString()}`;
      },
      providesTags: (result) =>
        result && result.data?.customers && Array.isArray(result.data.customers)
          ? [
            ...result.data.customers.map(({ id }) => ({ type: 'Customer' as const, id })),
            'Customer',
          ]
          : ['Customer'],
    }),

    // Get single customer
    getCustomer: builder.query<ApiResponse<Customer>, string>({
      query: (id) => `/customers/${id}`,
      providesTags: (_, __, id) => [{ type: 'Customer', id }],
    }),

    // Get customer analytics
    getCustomerAnalytics: builder.query<ApiResponse<CustomerAnalytics>, { id: string; storeId?: string }>({
      query: ({ id, storeId }) => {
        const params = new URLSearchParams();
        if (storeId) params.append('storeId', storeId);
        return `/customers/${id}/analytics?${params.toString()}`;
      },
      providesTags: (_, __, { id }) => [{ type: 'Customer', id }],
    }),

    // Create new customer
    createCustomer: builder.mutation<ApiResponse<Customer>, CreateCustomerData>({
      query: (data) => ({
        url: '/customers',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Customer', 'CustomerStats'],
    }),

    // Update customer
    updateCustomer: builder.mutation<
      ApiResponse<Customer>,
      { id: string; data: UpdateCustomerData }
    >({
      query: ({ id, data }) => ({
        url: `/customers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Customer', id }, 'CustomerStats'],
    }),

    // Delete customer
    deleteCustomer: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/customers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Customer', 'CustomerStats'],
    }),

    // Bulk delete customers
    bulkDeleteCustomers: builder.mutation<ApiResponse<void>, string[]>({
      query: (ids) => ({
        url: '/customers/bulk-delete',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['Customer', 'CustomerStats'],
    }),

    // Search customers (for autocomplete/quick search)
    searchCustomers: builder.query<ApiResponse<Customer[]>, { search: string; storeId?: string }>({
      query: ({ search, storeId }) => ({
        url: '/customers',
        params: {
          search,
          limit: 10,
          ...(storeId && { storeId }),
        },
      }),
      providesTags: ['Customer'],
    }),

    // Get customer transaction history
    getCustomerTransactions: builder.query<
      ApiResponse<CustomerTransactionHistory>,
      { customerId: string; page?: number; limit?: number }
    >({
      query: ({ customerId, page = 1, limit = 20 }) =>
        `/customers/${customerId}/transactions?page=${page}&limit=${limit}`,
      providesTags: (_, __, { customerId }) => [
        { type: 'Customer', id: customerId },
        'CustomerStats',
      ],
    }),

    // Get loyalty ledger (point history) for a customer
    getLoyaltyLedger: builder.query<
      ApiResponse<{ entries: LoyaltyLedgerEntry[]; total: number; page: number; limit: number }>,
      { customerId: string; page?: number; limit?: number }
    >({
      query: ({ customerId, page = 1, limit = 20 }) =>
        `/customers/${customerId}/loyalty-ledger?page=${page}&limit=${limit}`,
      providesTags: (_, __, { customerId }) => [{ type: 'Customer', id: customerId }],
    }),

    // Update customer loyalty points
    updateLoyaltyPoints: builder.mutation<
      ApiResponse<Customer>,
      { customerId: string; points: number; reason: string }
    >({
      query: ({ customerId, points, reason }) => ({
        url: `/customers/${customerId}/loyalty-points`,
        method: 'POST',
        body: { points, reason },
      }),
      invalidatesTags: (_, __, { customerId }) => [
        { type: 'Customer', id: customerId },
        'CustomerStats',
      ],
    }),

    // Get top customers
    getTopCustomers: builder.query<
      ApiResponse<Customer[]>,
      { limit?: number; sortBy?: 'totalSpent' | 'loyaltyPoints' | 'transactionCount'; storeId?: string }
    >({
      query: ({ limit = 10, sortBy = 'totalSpent', storeId }) => {
        const params = new URLSearchParams({
          limit: limit.toString(),
          sortBy,
        });
        if (storeId) params.append('storeId', storeId);
        return `/customers/top?${params.toString()}`;
      },
      providesTags: ['Customer'],
    }),

    // Get customer insights
    getCustomerInsights: builder.query<
      ApiResponse<CustomerInsights>,
      { period?: '3m' | '6m' | '1y'; storeId?: string }
    >({
      query: ({ period = '6m', storeId }) => {
        const params = new URLSearchParams({ period });
        if (storeId) params.append('storeId', storeId);
        return `/customers/insights?${params.toString()}`;
      },
      providesTags: ['CustomerStats'],
    }),

    // Bulk operations
    bulkImportCustomers: builder.mutation<
      ApiResponse<{
        imported: number;
        failed: number;
        errors: Array<{ row: number; error: string }>
      }>,
      { formData: FormData; storeId: string }
    >({
      query: ({ formData, storeId }) => ({
        url: `/customers/bulk-import?storeId=${storeId}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Customer', 'CustomerStats'],
    }),

    // Export customers - handle blob response manually to avoid serialization issues
    exportCustomers: builder.mutation<void, BulkCustomerExportParams>({
      queryFn: async (params, { getState }) => {
        try {
          const state = getState() as any;
          const token = state.auth.token;

          const response = await fetch(`${import.meta.env.VITE_API_URL || getApiConfig().API_URL}/customers/export`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Accept-Language': getRequestLanguage(),
            },
            body: JSON.stringify(params),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
            return { error: { status: response.status, data: errorData } };
          }

          const blob = await response.blob();

          // Handle download directly
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const extension = params.format === 'csv' ? 'csv' : params.format === 'excel' ? 'xlsx' : 'pdf';
          a.download = buildDownloadFilename('customers', extension);
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);

          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: { message: error.message } } };
        }
      },
    }),

    // Get import template - handle blob response manually
    getCustomerImportTemplate: builder.mutation<void, void>({
      queryFn: async (_, { getState }) => {
        try {
          const state = getState() as any;
          const token = state.auth.token;

          const response = await fetch(`${import.meta.env.VITE_API_URL || getApiConfig().API_URL}/customers/import-template`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept-Language': getRequestLanguage(),
            },
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Template download failed' }));
            return { error: { status: response.status, data: errorData } };
          }

          const blob = await response.blob();

          // Handle download directly
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = buildDownloadFilename('customerImportTemplate', 'xlsx', { omitDate: true });
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);

          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 'FETCH_ERROR', data: { message: error.message } } };
        }
      },
    }),

    // Send promotional message
    sendPromotionalMessage: builder.mutation<
      ApiResponse<{ sent: number; failed: number }>,
      {
        customerIds: string[];
        message: string;
        type: 'whatsapp' | 'sms' | 'email';
        storeName: string;
        scheduledAt?: string;
      }
    >({
      query: (data) => ({
        url: '/customers/send-promotion',
        method: 'POST',
        body: data,
      }),
    }),

    // Merge duplicate customers
    mergeDuplicateCustomers: builder.mutation<
      ApiResponse<Customer>,
      { primaryCustomerId: string; duplicateCustomerIds: string[] }
    >({
      query: (data) => ({
        url: '/customers/merge-duplicates',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Customer', 'CustomerStats'],
    }),
  }),
});

export const {
  useGetCustomerStatsQuery,
  useGetCustomersQuery,
  useGetCustomerQuery,
  useGetCustomerAnalyticsQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useBulkDeleteCustomersMutation,
  useSearchCustomersQuery,
  useGetCustomerTransactionsQuery,
  useUpdateLoyaltyPointsMutation,
  useGetTopCustomersQuery,
  useGetCustomerInsightsQuery,
  useBulkImportCustomersMutation,
  useExportCustomersMutation,
  useGetCustomerImportTemplateMutation,
  useSendPromotionalMessageMutation,
  useMergeDuplicateCustomersMutation,
  useGetLoyaltyLedgerQuery,
} = customerApi;
